import type { AllowedFileType } from '@/types/api/file'

/** Fields a file must satisfy (global policy + optional per-field constraints). */
export interface FileRules {
  allowedTypes?: string[] | null
  minFileSizeBytes?: number | null
  maxFileSizeBytes?: number | null
}

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  txt: 'text/plain',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  zip: 'application/zip',
}

/** Human-readable byte size (e.g. "1.4 MB"). */
export function formatBytes(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function fileExtension(name?: string | null): string {
  if (!name) return ''
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''
}

/** Matches the full dashed-UUID prefix used in S3 key last segments: `{uuid}-{name}`. */
const DASHED_UUID_PREFIX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-/

/** Extracts a readable name from an S3 key (`{uuid}-{name}` is the last segment). */
export function fileNameFromKey(key: string): string {
  const segment = key.split('/').pop() ?? key
  // Full dashed-UUID prefix (the UUID itself contains dashes, so a naive first-dash
  // split would leave UUID fragments behind).
  const uuidMatch = segment.match(DASHED_UUID_PREFIX)
  if (uuidMatch) return segment.slice(uuidMatch[0].length)
  // Compact hex / short token prefix: `abc123-name.ext` / `32hexchars-name.ext`.
  const dash = segment.indexOf('-')
  return dash >= 0 && dash < segment.length - 1 ? segment.slice(dash + 1) : segment
}

/** Directory markers used by the storage module when building S3 keys. */
const FILE_KEY_DIRECTORY_MARKERS = ['/form-values/', '/public-form-values/', '/activities/']

/**
 * True when a stored field value is an S3 object key (workspace/{recordGroup}/…).
 * Used as a defensive fallback when an activity row predates the field-type snapshot
 * (or was written by a path that didn't capture it) — never matches normal text values.
 */
export function isFileStorageKey(value: string | null | undefined): boolean {
  if (!value) return false
  return FILE_KEY_DIRECTORY_MARKERS.some((marker) => value.includes(marker))
}

/** Default and largest number of files a multiple-file field may hold. */
export const DEFAULT_MAX_FILES = 10
export const MAX_FILES_LIMIT = 20

/**
 * The keys in a stored FILE_UPLOAD value (mirrors the backend's FileFieldValues): none for an empty
 * value, the list's keys for a JSON array, otherwise the value itself as the one key.
 */
export function parseFileKeys(raw: string | null | undefined): string[] {
  const value = raw?.trim()
  if (!value) return []
  if (value.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
      }
    } catch {
      // not a list after all: a key never starts with "[", so this does not happen in practice
    }
  }
  return [value]
}

/** The value to store: '' for no files, the plain key for a single-file field, a JSON array for several. */
export function formatFileKeys(keys: string[], multiple: boolean): string {
  if (keys.length === 0) return ''
  return multiple ? JSON.stringify(keys) : keys[0]
}

/** True when a FILE_UPLOAD field is set to hold several files (`options.selectionMode`). */
export function isMultipleFileField(options: Record<string, unknown> | null | undefined): boolean {
  return String(options?.selectionMode ?? 'SINGLE').toUpperCase() === 'MULTIPLE'
}

/** Most files a field holds: 1 for single-file fields, `validations.maxFiles` (default 10) for multiple. */
export function maxFilesForField(
  options: Record<string, unknown> | null | undefined,
  validations: Record<string, unknown> | null | undefined
): number {
  if (!isMultipleFileField(options)) return 1
  const configured = validations?.maxFiles
  return typeof configured === 'number' && configured >= 1 ? Math.min(configured, MAX_FILES_LIMIT) : DEFAULT_MAX_FILES
}

/** File names of a stored FILE_UPLOAD value, in order. */
export function fileNamesFromValue(raw: string | null | undefined): string[] {
  return parseFileKeys(raw).map(fileNameFromKey)
}

/**
 * Display form of an activity field value: file keys are shortened to just the file name
 * (several are joined), everything else is shown verbatim.
 */
export function displayActivityValue(
  value: string | null | undefined,
  fieldType?: string | null
): string {
  if (value == null || value === '') return '—'
  if (fieldType === 'FILE_UPLOAD') {
    const names = fileNamesFromValue(value)
    return names.length ? names.join(', ') : '—'
  }
  return isFileStorageKey(value) ? fileNameFromKey(value) : value
}

/** MIME type of the file: the browser value when specific, else inferred from the name. */
export function resolveMimeType(file: File): string | null {
  if (file.type && file.type !== 'application/octet-stream') return file.type
  return MIME_BY_EXTENSION[fileExtension(file.name)] ?? null
}

export function isImageFile(contentType?: string | null, name?: string | null): boolean {
  if (contentType?.startsWith('image/')) return true
  return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension(name))
}

export function isPdfFile(contentType?: string | null, name?: string | null): boolean {
  return contentType === 'application/pdf' || fileExtension(name) === 'pdf'
}

/** Client-side mirror of the backend upload checks. Returns an error message or null. */
export function validateFileForUpload(file: File, rules: FileRules = {}): string | null {
  if (file.size <= 0) return 'The selected file is empty'

  const max = rules.maxFileSizeBytes ?? null
  if (max != null && file.size > max) {
    return `File is larger than the maximum allowed size of ${formatBytes(max)}`
  }

  const min = rules.minFileSizeBytes ?? null
  if (min != null && file.size < min) {
    return `File is smaller than the minimum allowed size of ${formatBytes(min)}`
  }

  const allowed = (rules.allowedTypes ?? []).filter(Boolean)
  if (allowed.length > 0) {
    const resolved = resolveMimeType(file)
    if (!resolved || !allowed.includes(resolved)) {
      return 'This file type is not allowed'
    }
  }

  return null
}

/** `accept` attribute value for the file input, based on the effective constraints. */
export function buildAcceptAttribute(
  rules: FileRules,
  catalog: AllowedFileType[] = []
): string | undefined {
  const allowed = (rules.allowedTypes ?? []).filter(Boolean)
  if (allowed.length === 0) return undefined

  const tokens = new Set<string>()
  for (const entry of catalog) {
    if (!allowed.includes(entry.value)) continue
    tokens.add(entry.value)
    entry.mimeTypes?.forEach((mime) => tokens.add(mime))
    entry.extensions?.forEach((ext) => tokens.add(`.${ext}`))
  }
  // Fall back to the raw values when the catalog does not know the type.
  allowed.forEach((value) => tokens.add(value))

  return Array.from(tokens).join(',')
}

/** Label for a MIME type using the served catalog (falls back to the raw value). */
export function fileTypeLabel(value: string, catalog: AllowedFileType[] = []): string {
  return catalog.find((entry) => entry.value === value)?.label ?? value
}

/**
 * Effective upload rules for a field: per-field constraints when present, falling back
 * to the global per-file maximum. `validations` is the raw JSONB map from the form field.
 */
export function fileRulesForField(
  validations: Record<string, unknown> | null | undefined,
  globalMaxFileSizeBytes?: number | null
): FileRules {
  const v = validations ?? {}
  return {
    allowedTypes: Array.isArray(v.allowedFileTypes) ? (v.allowedFileTypes as string[]) : null,
    minFileSizeBytes: typeof v.minFileSizeBytes === 'number' ? v.minFileSizeBytes : null,
    maxFileSizeBytes: typeof v.maxFileSizeBytes === 'number'
      ? v.maxFileSizeBytes
      : globalMaxFileSizeBytes ?? null,
  }
}


/**
 * Saves a file. The attachment URL tells the browser to save it, so following it in place starts a
 * download and the page stays put. Only an older server without it falls back to a new tab.
 */
export function saveFile(attachmentUrl?: string | null, viewUrl?: string | null): void {
  if (attachmentUrl) {
    const link = document.createElement('a')
    link.href = attachmentUrl
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
  } else if (viewUrl) {
    window.open(viewUrl, '_blank', 'noopener,noreferrer')
  }
}
